<template>
  <div class="tableWithPerPageSelection">
    <div class="entete border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">{{ $t('listeUtilisateur.title') }}</h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button is-addOrEdit"
                    :class="{
                   'button-access-no-permission' : !checkPermissions(permsCollaborateurs.maj)
                   }"
                    @click="$router.push({name: 'm.parametrage.collaborateurs-add' })">
              {{ $t('listeUtilisateur.createCollaborateur') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="level mgt-20px">
      <div class="level-left">
        <div class="columns">
          <form @submit.prevent="filter = filterInput" class="column is-12">
            <b-input :placeholder="$t('listeDevis.actions.search')"
                     type="search"
                     icon="magnify"
                     v-model="filterInput">
            </b-input>
          </form>
        </div>
      </div>
      <div class="level-right columns">
        <div class="column is-narrow">
          <a @click="displayInactif = !displayInactif">
            <span class="has-text-primary lien">
              {{ displayInactif
                ? $t('listeUtilisateur.actions.utilisateurArchive',
                  { nbre: getInactifNumber })
                : $t('listeUtilisateur.actions.masquerUtlisateurArchiver',
                  { nbre: getInactifNumber })
              }}
            </span>
          </a>
        </div>
        <div class="column is-narrow">
          <exportExcelButton
            :perm="permsCollaborateurs.exporter"
            in-progress/>
        </div>
      </div>
    </div>
    <v-client-table :data="filteredUtilisateurs"
                      :columns="columns"
                      :options="options"
                      ref="tableClient"
                      @pagination="getLogoUtilisateur">
        <template slot='afterTable'>
          <table-items-in-view @input="$refs.tableClient.setLimit($event)"/>
        </template>
        <div slot="avatar" class="has-text-centered" slot-scope="props">
          <avatar :username="getUsername(props.row)" :size="32"
                  :src="props.row.logo"
          >
          </avatar>
        </div>
        <div slot="identifiant" slot-scope="props">
          <span v-if="props.row.identifiant === 'quadraadmin'">
            {{ props.row.identifiant }}
          </span>
          <router-link v-else :to="{ name: 'c.parametrage.collaborateurs',
          params: { id: props.row.id }}" class="lien"
                       :class="{'text-access-no-permission':
                                   !checkPermissions(permsCollaborateurs.lecture)}">
            {{ props.row.identifiant }}
          </router-link>
        </div>
        <div slot="civilite" slot-scope="props">
          {{ getCiviliteLibelle(props.row.civiliteId) }}
        </div>
        <div slot="profil" slot-scope="props">
          {{ $t(getLibelleProfil(props.row.profil)) }}
        </div>
        <div slot="modifier" class="has-text-centered" slot-scope="props">
          <a @click="$router.push({
          name: 'm.parametrage.collaborateurs-edit', params: { id: props.row.id }
          })" v-if="props.row.identifiant !== 'quadraadmin'">
            <b-icon
              icon="pencil-box-outline"
              type="is-success"
              :class="{
              'text-access-no-permission' : !checkPermissions(permsCollaborateurs.maj)
              }"
            >
            </b-icon>
          </a>
        </div>
        <template slot="archiver" slot-scope="{ row }">
          <archive-list-button
            v-if="row.identifiant != 'quadraadmin'"
            v-bind="getDataPopupArchivage(row)"
            :query="(event) => archiverUtilisateur(row, event)"
            :permissions="permsCollaborateurs.supprimer"
            @done="initialize"
            class="has-text-centered"
            :actif="row.actif"/>
        </template>
        <div slot="supprimer" class="has-text-centered"  slot-scope="props">
          <a @click="checkPermissions(permsCollaborateurs.supprimer) ? inProgress() : showPopUp()"
             v-if="props.row.identifiant != 'quadraadmin'">
            <b-icon
              icon="delete-forever"
              type="is-danger"
              :class="{
              'text-access-no-permission' : !checkPermissions(permsCollaborateurs.supprimer)
              }"
            >
            </b-icon>
          </a>
        </div>
    </v-client-table>
  </div>
</template>

<script>

import Avatar from 'vue-avatar';
import { createNamespacedHelpers } from 'vuex';
import AppBreadcrumb from '@/components/general/breadcrumb';
import inProgress from '@/services/general/inProgress';
import messageErreur from '@/services/general/messageErreur';
import intToGuid from '@/services/general/transformIntToGuid';
import getLogo from '@/services/general/getLogo';
import {
  TEXTS, RESULTS_PER_PAGE, NO_PER_PAGE_VALUE, SORT_ICON,
} from '@/configuration/clientTableConf';
import getListeUtilisateur from '@/services/utilisateur/formatListeUtilisateurs';
import * as typesParametres from '@/store/modules/parametres-mutation-type';
import { COLLABORATEURS } from '@/configuration/general/permissionsParametrageConf';
import { LISTE_PROFIL } from '@/configuration/parametrage/utilisateurConf';
import TableItemsInView from '@/components/general/TableItemsInView';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import nomPrenomGetter from '@/services/general/nomPrenomGetter';
import { archiver } from '@/api/utilisateur/index';
import archiveListButton from '@/components/general/button/archiveListButton';

const Parametres = createNamespacedHelpers('parametres');
const permissions = createNamespacedHelpers('permissions');

export default {
  components: {
    AppBreadcrumb,
    Avatar,
    TableItemsInView,
    archiveListButton,
    exportExcelButton,
  },
  data() {
    return {
      listeProfil: LISTE_PROFIL,
      utilisateurs: [],
      filter: '',
      filterInput: '',
      permsCollaborateurs: COLLABORATEURS,
      displayInactif: true,
    };
  },
  computed: {
    ...permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    breadcrumb() {
      const output = [
        { title: this.$i18n.t('listeUtilisateur.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('listeUtilisateur.parametrage'), route: { name: 'l.parametrage.general' }, isActive: false },
        { title: this.$i18n.t('listeUtilisateur.title'), route: { name: 'l.parametrage.collaborateurs' }, isActive: true },
      ];
      return output;
    },
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    civilites() {
      return this.parametre(typesParametres.PARAMETRES_KEYS.civilite);
    },
    columns() {
      return [
        'avatar',
        'identifiant',
        'role',
        'civilite',
        'nom',
        'prenom',
        'profil',
        'telephone',
        'email',
        'modifier',
        'archiver',
        'supprimer',
      ];
    },
    options() {
      return {
        headings: {
          identifiant: this.$i18n.t('listeUtilisateur.tab.identifiant'),
          role: this.$i18n.t('listeUtilisateur.tab.role'),
          civilite: this.$i18n.t('listeUtilisateur.tab.civilite'),
          nom: this.$i18n.t('listeUtilisateur.tab.nom'),
          prenom: this.$i18n.t('listeUtilisateur.tab.prenom'),
          telephone: this.$i18n.t('listeUtilisateur.tab.tel'),
          email: this.$i18n.t('listeUtilisateur.tab.mail'),
          profil: this.$i18n.t('listeUtilisateur.tab.profil'),
          modifier: '',
          archiver: '',
          supprimer: '',
          avatar: '',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: TEXTS,
        sortIcon: SORT_ICON,
        sortable: [
          'identifiant',
          'role',
          'civilite',
          'nom',
          'prenom',
          'telephone',
          'email',
          'profil',
        ],
        columnsClasses: {
          avatar: 'colonneSelected',
          archiver: 'colonneSelected',
          supprimer: 'colonneSelected',
          modifier: 'colonneSelected',
        },
      };
    },
    filteredUtilisateurs() {
      const users = this.utilisateurs.filter(utilisateur => {
        const utilisateursSearch = [
          utilisateur.nom,
          utilisateur.prenom || '',
          utilisateur.identifiant,
        ];
        let find = false;
        utilisateursSearch.forEach((utilisateurSearch) => {
          if (utilisateurSearch.toLowerCase().includes(this.filter.toLowerCase())) {
            find = true;
          }
        });
        return find;
      });
      return users.filter(user => user.actif === this.displayInactif);
    },
    getInactifNumber() {
      return this.utilisateurs.filter((user) => { return user.actif === false; }).length;
    },
  },
  methods: {
    ...permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
      loadParametreUtilisateur: 'loadParametreUtilisateur',
      updateUserToParametre: 'updateUtilisateurToParametre',
    }),
    getUsername(user) {
      return nomPrenomGetter(user);
    },
    getLibelleProfil(profil) {
      return this.listeProfil.find((typeProfil) => {
        return typeProfil.code === profil;
      }).libelle;
    },
    inProgress(message) {
      inProgress(this.$i18n, message);
    },
    async initialize() {
      this.utilisateurs = await getListeUtilisateur();
    },
    async getLogoUtilisateur() {
      const logoPromise = [];
      this.$refs.tableClient.filteredData.forEach((utilisateur) => {
        if (!utilisateur.logoCharge) {
          logoPromise.push(getLogo(intToGuid(utilisateur.id)));
        }
      });
      const allLogos = await Promise.all(logoPromise);
      for (let i = 0; i < this.utilisateurs.length; i += 1) {
        if (!this.utilisateurs[i].logo) {
          this.utilisateurs[i].logo = allLogos[i];
        }
        const checkIfUtilisateurVisible = this.$refs.tableClient.filteredData.find((el) => {
          return el.id === this.utilisateurs[i].id;
        });
        if (checkIfUtilisateurVisible) {
          this.utilisateurs[i].logoCharge = true;
        }
      }
      return this.utilisateurs;
    },
    getCiviliteLibelle(civiliteId) {
      const civilite = this.civilites.find((civilite) => {
        return civilite.id === civiliteId;
      });
      return civilite ? civilite.libelle : '';
    },
    getDataPopupArchivage(user) {
      return {
        title: user.actif
          ? this.$t('listeUtilisateur.popUpArchivageDesarchivage.titleArchivage')
          : this.$t('listeUtilisateur.popUpArchivageDesarchivage.titleDesarchivage'),
        messDescription: user.actif
          ? this.$t('listeUtilisateur.popUpArchivageDesarchivage.infoArchivageDesarchivage.archivage')
          : '',
        questions: user.actif ? [] : [{
          key: 0,
          message: 'listeUtilisateur.popUpArchivageDesarchivage.infoArchivageDesarchivage.desarchivage',
          value: false,
        }],
        successMessage: user.actif
          ? this.$i18n.t('listeUtilisateur.popUpArchivageDesarchivage.success.archivage')
          : this.$i18n.t('listeUtilisateur.popUpArchivageDesarchivage.success.desarchivage'),
      };
    },
    async archiverUtilisateur(user, values) {
      let connexionAutorisee = false;
      if (values?.length > 0) {
        const find = values.find(v => v.key === 0);
        if (find) connexionAutorisee = find.value;
      }
      await archiver({
        id: user.id,
        archiver: user.actif,
        connexionAutorisee: connexionAutorisee,
      });
      user.actif = !user.actif;
      user.connexionAutorisee = connexionAutorisee;
      this.updateUserToParametre(user);
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      this.setInformationPage(this.$t('pageParametrage.collaborateurs.liste'));
      await this.initialize();
      await this.loadParametres([typesParametres.PARAMETRES_KEYS.civilite]);
      await this.loadParametreUtilisateur();
      loadingComponent.close();
      await this.getLogoUtilisateur();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>
